import { papaSharedCookieDomain } from './consent.js'

// Browser half of consumer signup ad attribution.
//
// Google ("Set up offline conversions using Google Click ID"): read the click
// ID from the URL, persist it yourself in first-party storage for 90 days, and
// read it back at form submit. Papa keeps exactly one cookie, `papa_gcl`,
// holding the most recent Google click of any kind as `<kind>.<value>`.
//
// Meta ("ClickID and the fbp and fbc parameters"): send the `_fbc`/`_fbp`
// cookie values when present. When the URL carries an fbclid and `_fbc` is
// absent or holds a different fbclid, write `_fbc` yourself as
// `fb.1.<observed ms>.<fbclid>` for 90 days. `_fbp` is only ever the Pixel's.
//
// Click IDs are observed, persisted, and held in memory only while Marketing
// consent is granted, and are emitted only when the live consent read at the
// final submit is granted. Pure functions plus a store factory: no reads of
// `window`/`document` at import, no timers, no retries.

export const GOOGLE_CLICK_ID_KINDS = ['gclid', 'gbraid', 'wbraid']
export const PAPA_GOOGLE_CLICK_COOKIE = 'papa_gcl'
export const META_FBC_COOKIE = '_fbc'
export const META_FBP_COOKIE = '_fbp'
export const AD_CLICK_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60
export const AD_IDENTIFIER_MAX_BYTES = 2048

// Whitespace and control characters never belong in a click ID; `;`, `,`, `"`
// and `\` are cookie-hostile (a `;` in a value would terminate it and inject
// attributes when `_fbc` is written raw to match the Pixel's format).
const AD_IDENTIFIER_FORBIDDEN_CHARACTERS = /[\u0000-\u001f\u007f\s;,"\\]/u
const META_IDENTIFIER_FORMAT = /^fb\.\d{1,2}\.\d{10,16}\.\S+$/u
const META_CREATION_TIME_MAX_SKEW_MS = 5 * 60 * 1000
const utf8 = new TextEncoder()

// --- consent -----------------------------------------------------------------

// Osano's live Marketing state is the only authority. `initialized` separates a
// real denial from "no Osano callback has fired yet" (script missing, failed,
// or still loading). `osanoDisabled` follows the lower-environment convention
// shared by every other browser Ads gate.
export function readMarketingConsentState({ osanoDisabled, initialized, hasMarketingConsent }) {
  if (osanoDisabled) return 'granted'
  if (!initialized) return 'unknown'
  return hasMarketingConsent ? 'granted' : 'denied'
}

export function readMarketingConsentSource({ osanoDisabled, initialized }) {
  if (osanoDisabled) return 'osano_disabled'
  return initialized ? 'osano' : 'osano_unavailable'
}

// --- bounds ------------------------------------------------------------------

// Identifiers are never truncated or case-modified: an out-of-bounds value is
// dropped as a whole.
export function isBoundedAdIdentifier(value) {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    !AD_IDENTIFIER_FORBIDDEN_CHARACTERS.test(value) &&
    utf8.encode(value).length <= AD_IDENTIFIER_MAX_BYTES
  )
}

// --- cookies -----------------------------------------------------------------

export function readCookieValue(cookieString, name) {
  for (const part of String(cookieString || '').split(';')) {
    const [cookieName, ...valueParts] = part.trim().split('=')

    if (cookieName === name) return valueParts.join('=')
  }

  return null
}

// 90 days, site-wide, Lax. `Secure` only on https (review apps and localhost
// run plain http). `Domain=papa.com` on Papa hosts so the cookie is one cookie
// across Papa properties -- the same scope the Pixel picks for `_fbc`.
export function formatAdCookie(name, value, { hostname, protocol }) {
  const cookie = [
    `${name}=${value}`,
    `Max-Age=${AD_CLICK_COOKIE_MAX_AGE_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
  ]

  if (protocol === 'https:') cookie.push('Secure')

  const sharedDomain = papaSharedCookieDomain(hostname)
  if (sharedDomain) cookie.push(`Domain=${sharedDomain}`)

  return cookie.join('; ')
}

// --- URL click IDs -----------------------------------------------------------

// Allowlisted keys only. One Google click carries exactly one kind; if a URL
// anomalously carries several, precedence is gclid > gbraid > wbraid. Google's
// documented check: a `gclsrc` without "aw" marks a click that did not come
// from Google Ads, so its gclid is ignored.
export function parseClickIds(search = '') {
  const params = new URLSearchParams(search)
  const gclsrc = params.get('gclsrc')
  const gclidAllowed = !gclsrc || gclsrc.includes('aw')
  let google = null

  for (const kind of GOOGLE_CLICK_ID_KINDS) {
    if (kind === 'gclid' && !gclidAllowed) continue

    const value = params.get(kind)

    if (isBoundedAdIdentifier(value)) {
      google = { kind, value }
      break
    }
  }

  const fbclid = params.get('fbclid')

  return { google, fbclid: isBoundedAdIdentifier(fbclid) ? fbclid : null }
}

// --- Google persistence ------------------------------------------------------

// `<kind>.<value>`; the value is percent-encoded so a cookie-hostile character
// can never truncate it. Real click IDs are URL-safe and encode to themselves.
export function formatPapaGoogleCookie({ kind, value }) {
  return `${kind}.${encodeURIComponent(value)}`
}

export function readPapaGoogleCookie(cookieString) {
  const raw = readCookieValue(cookieString, PAPA_GOOGLE_CLICK_COOKIE)
  if (!raw) return null

  const separator = raw.indexOf('.')
  if (separator < 0) return null

  const kind = raw.slice(0, separator)
  const value = decodeCookieValue(raw.slice(separator + 1))

  if (!GOOGLE_CLICK_ID_KINDS.includes(kind) || !isBoundedAdIdentifier(value)) return null

  return { kind, value }
}

function decodeCookieValue(value) {
  try {
    return decodeURIComponent(value)
  } catch {
    return null
  }
}

// --- Meta --------------------------------------------------------------------

// Subdomain index 1 = the registrable domain, as Meta specifies for
// self-generated values; the creation time is when the fbclid was first seen.
export function formatFbc(fbclid, observedAtMs) {
  return `fb.1.${observedAtMs}.${fbclid}`
}

// `fb.<subdomain index>.<creation ms>.<click id>`; Meta's parameter builder may
// append a suffix to the click id, which `\S+` tolerates. A creation time in
// the future (beyond clock skew) is not a cookie the Pixel or Papa wrote.
export function isValidMetaIdentifier(value, nowMs) {
  if (!isBoundedAdIdentifier(value) || !META_IDENTIFIER_FORMAT.test(value)) return false

  const creationTimeMs = Number(value.split('.')[2])

  return creationTimeMs <= nowMs + META_CREATION_TIME_MAX_SKEW_MS
}

export function readMetaCookies(cookieString, nowMs) {
  const fbc = readCookieValue(cookieString, META_FBC_COOKIE)
  const fbp = readCookieValue(cookieString, META_FBP_COOKIE)

  return {
    fbc: isValidMetaIdentifier(fbc, nowMs) ? fbc : null,
    fbp: isValidMetaIdentifier(fbp, nowMs) ? fbp : null,
  }
}

// The fbclid an `_fbc` cookie was built from is everything after its last dot.
function fbcClickId(fbc) {
  return fbc.slice(fbc.lastIndexOf('.') + 1)
}

// --- store -------------------------------------------------------------------

// One per page, created by app.js at load and fed from the Osano consent
// callback (or the OSANO_DISABLED branch). The request-form hook reads
// `snapshot()` at the final submit. Browser accessors are injectable for tests.
export function createAdClickIdStore({
  getSearch = () => window.location.search,
  getCookie = () => document.cookie,
  setCookie = cookie => {
    document.cookie = cookie
  },
  now = () => Date.now(),
  hostname = window.location.hostname,
  protocol = window.location.protocol,
} = {}) {
  const cookieScope = { hostname, protocol }
  let initialized = false
  // Observed from this page's URL while Marketing consent was granted.
  let google = null // { kind, value }
  let metaClick = null // { fbclid, observedAtMs }

  // Replace the Papa cookie whenever a Google click of any kind is observed so
  // a stale click of one kind is never paired with a newer click of another.
  function observeGoogleClick(click) {
    google = click

    const cookieValue = formatPapaGoogleCookie(click)

    if (readCookieValue(getCookie(), PAPA_GOOGLE_CLICK_COOKIE) !== cookieValue) {
      setCookie(formatAdCookie(PAPA_GOOGLE_CLICK_COOKIE, cookieValue, cookieScope))
    }
  }

  // Meta's recommendation: write `_fbc` when it is absent or holds a different
  // fbclid than the URL. Otherwise leave the Pixel's cookie alone.
  function observeMetaClick(fbclid) {
    if (!metaClick || metaClick.fbclid !== fbclid) {
      metaClick = { fbclid, observedAtMs: now() }
    }

    const existing = readCookieValue(getCookie(), META_FBC_COOKIE)

    if (existing === null || fbcClickId(existing) !== fbclid) {
      setCookie(
        formatAdCookie(META_FBC_COOKIE, formatFbc(fbclid, metaClick.observedAtMs), cookieScope),
      )
    }
  }

  return {
    markInitialized() {
      initialized = true
    },

    // Granted: observe the current URL (memory + cookies). Not granted: drop
    // memory; consent.js expires the cookies through the Marketing list.
    // Idempotent, so every consent callback and every reconnect can call it.
    applyConsent({ granted }) {
      if (!granted) {
        google = null
        metaClick = null
        return
      }

      const clickIds = parseClickIds(getSearch())

      if (clickIds.google) observeGoogleClick(clickIds.google)
      if (clickIds.fbclid) observeMetaClick(clickIds.fbclid)
    },

    // The payload pushed at the final submit. Identifiers only under a live
    // granted read; the raw fbclid is never part of it.
    snapshot({ osanoDisabled = false, hasMarketingConsent = false } = {}) {
      const consent = {
        osanoDisabled: Boolean(osanoDisabled),
        initialized,
        hasMarketingConsent: Boolean(hasMarketingConsent),
      }
      const payload = {
        marketing_consent_state: readMarketingConsentState(consent),
        consent_source: readMarketingConsentSource(consent),
      }

      if (payload.marketing_consent_state !== 'granted') return payload

      const cookies = getCookie()
      const googleClick = google || readPapaGoogleCookie(cookies)

      if (googleClick) payload[googleClick.kind] = googleClick.value

      const meta = readMetaCookies(cookies, now())
      const fbc = meta.fbc || (metaClick && formatFbc(metaClick.fbclid, metaClick.observedAtMs))

      if (fbc) payload.fbc = fbc
      if (meta.fbp) payload.fbp = meta.fbp

      return payload
    },
  }
}
